import { useState, useEffect } from 'react';
import type { Product, Category, MonthlyPackage, MonthlyDuration, PackagePlan } from '../types';
import { categories as allCategories, datacenters, operatingSystems, pcFarmModels } from '../mockData';
import MonthlyPackageModal from './MonthlyPackageModal';
import MonthlyDurationManager from './MonthlyDurationManager';
import PackagePlanModal from './PackagePlanModal';

interface ProductFormModalProps {
  isOpen: boolean;
  product: Product | null;
  onClose: () => void;
  onSave: (product: Partial<Product>) => void;
}

export default function ProductFormModal({ isOpen, product, onClose, onSave }: ProductFormModalProps) {
  const [formData, setFormData] = useState<Partial<Product>>({
    name: '',
    categoryId: 0,
    isOnline: true,
    sort: 999,
    tags: [],
    description: '',
    configType: 'cpu',
    cpuCores: 4,
    memory: 8,
    systemDisk: 100,
    bandwidth: { download: 50, upload: 20 },
    operatingSystems: [],
    limitPerUser: 1,
    datacenters: [],
    salesPeriod: { type: 'permanent' },
    targetUsers: [],
    pricingMethods: [],
  });

  const [activeTab, setActiveTab] = useState<'basic' | 'config' | 'sales' | 'pricing'>('basic');
  const [subCategories, setSubCategories] = useState<Category[]>([]);
  const [showMonthlyPackageModal, setShowMonthlyPackageModal] = useState(false);
  const [editingMonthlyPackage, setEditingMonthlyPackage] = useState<MonthlyPackage | null>(null);
  const [showPackagePlanModal, setShowPackagePlanModal] = useState(false);
  const [editingPackagePlan, setEditingPackagePlan] = useState<PackagePlan | null>(null);

  useEffect(() => {
    if (product) {
      setFormData(product);
    } else {
      setFormData({
        name: '',
        categoryId: 0,
        isOnline: true,
        sort: 999,
        tags: [],
        description: '',
        configType: 'cpu',
        cpuCores: 4,
        memory: 8,
        systemDisk: 100,
        bandwidth: { download: 50, upload: 20 },
        operatingSystems: [],
        limitPerUser: 1,
        datacenters: [],
        salesPeriod: { type: 'permanent' },
        targetUsers: [],
        pricingMethods: [],
      });
    }
  }, [product]);

  const handleCategoryChange = (categoryId: number) => {
    const category = allCategories.find((c) => c.id === categoryId);
    setSubCategories(category?.children || []);
    setFormData({ ...formData, categoryId });
  };

  const handleSave = () => {
    onSave(formData);
  };

  const handleSaveMonthlyPackage = (packageData: Partial<MonthlyPackage>) => {
    const packages = formData.monthlyPackages || [];
    if (editingMonthlyPackage) {
      const updated = packages.map((p) =>
        p.id === editingMonthlyPackage.id ? { ...p, ...packageData } : p
      );
      setFormData({ ...formData, monthlyPackages: updated });
    } else {
      const newPackage: MonthlyPackage = {
        id: Math.max(0, ...packages.map((p) => p.id)) + 1,
        ...packageData,
      } as MonthlyPackage;
      setFormData({ ...formData, monthlyPackages: [...packages, newPackage] });
    }
    setShowMonthlyPackageModal(false);
    setEditingMonthlyPackage(null);
  };

  const handleDeleteMonthlyPackage = (id: number) => {
    const packages = formData.monthlyPackages || [];
    setFormData({ ...formData, monthlyPackages: packages.filter((p) => p.id !== id) });
  };

  const handleToggleMonthlyPackage = (id: number, isEnabled: boolean) => {
    const packages = formData.monthlyPackages || [];
    const updated = packages.map((p) => (p.id === id ? { ...p, isEnabled } : p));
    setFormData({ ...formData, monthlyPackages: updated });
  };

  const handleSaveMonthlyDurations = (durations: MonthlyDuration[]) => {
    setFormData({ ...formData, monthlyDurations: durations });
  };

  const handleSavePackagePlan = (planData: Partial<PackagePlan>) => {
    const plans = formData.packagePlans || [];
    if (editingPackagePlan) {
      const updated = plans.map((p) =>
        p.id === editingPackagePlan.id ? { ...p, ...planData } : p
      );
      setFormData({ ...formData, packagePlans: updated });
    } else {
      const newPlan: PackagePlan = {
        id: Math.max(0, ...plans.map((p) => p.id)) + 1,
        ...planData,
      } as PackagePlan;
      setFormData({ ...formData, packagePlans: [...plans, newPlan] });
    }
    setShowPackagePlanModal(false);
    setEditingPackagePlan(null);
  };

  const handleDeletePackagePlan = (id: number) => {
    const plans = formData.packagePlans || [];
    setFormData({ ...formData, packagePlans: plans.filter((p) => p.id !== id) });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto">
      <div className="absolute inset-0 bg-black/50" onClick={onClose}></div>
      <div className="relative bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h3 className="text-xl font-semibold text-gray-900">
            {product ? '编辑商品' : '新建商品'}
          </h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        <div className="border-b border-gray-200">
          <div className="flex gap-8 px-6">
            {[
              { key: 'basic', label: '基本信息' },
              { key: 'config', label: '配置规格' },
              { key: 'sales', label: '售卖规则' },
              { key: 'pricing', label: '收费方式' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as any)}
                className={`py-4 border-b-2 transition-colors whitespace-nowrap ${
                  activeTab === tab.key
                    ? 'border-teal-500 text-teal-600 font-medium'
                    : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'basic' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  商品名称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="请输入商品名称"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    一级分类 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.categoryId}
                    onChange={(e) => handleCategoryChange(Number(e.target.value))}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value={0}>请选择分类</option>
                    {allCategories.map((cat) => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>

                {subCategories.length > 0 && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">二级分类</label>
                    <select className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent">
                      <option value="">请选择二级分类</option>
                      {subCategories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">商品描述</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="请输入商品描述"
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    是否上架 <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-4">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.isOnline === true}
                        onChange={() => setFormData({ ...formData, isOnline: true })}
                        className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">是</span>
                    </label>
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.isOnline === false}
                        onChange={() => setFormData({ ...formData, isOnline: false })}
                        className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">否</span>
                    </label>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    排序权重 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={formData.sort}
                    onChange={(e) => setFormData({ ...formData, sort: Number(e.target.value) })}
                    placeholder="数字越大越靠前"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">商品标签</label>
                <div className="flex gap-2">
                  {['新品', '热卖', '促销'].map((tag) => (
                    <label key={tag} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.tags?.includes(tag)}
                        onChange={(e) => {
                          const tags = formData.tags || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, tags: [...tags, tag] });
                          } else {
                            setFormData({ ...formData, tags: tags.filter((t) => t !== tag) });
                          }
                        }}
                        className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">{tag}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'config' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">配置类型</label>
                <div className="flex gap-4">
                  {[
                    { value: 'cpu', label: 'CPU服务器' },
                    { value: 'gpu', label: 'GPU服务器' },
                    { value: 'pcfarm', label: 'PC Farm' },
                  ].map((type) => (
                    <label key={type.value} className="flex items-center cursor-pointer">
                      <input
                        type="radio"
                        checked={formData.configType === type.value}
                        onChange={() => setFormData({ ...formData, configType: type.value as any })}
                        className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.configType === 'pcfarm' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    PC Farm型号 <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={formData.pcFarmModel}
                    onChange={(e) => setFormData({ ...formData, pcFarmModel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="">请选择型号</option>
                    {pcFarmModels.map((model) => (
                      <option key={model} value={model}>
                        {model}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        CPU核心数 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.cpuCores}
                        onChange={(e) => setFormData({ ...formData, cpuCores: Number(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value={2}>2核</option>
                        <option value={4}>4核</option>
                        <option value={8}>8核</option>
                        <option value={16}>16核</option>
                        <option value={32}>32核</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        内存容量 <span className="text-red-500">*</span>
                      </label>
                      <select
                        value={formData.memory}
                        onChange={(e) => setFormData({ ...formData, memory: Number(e.target.value) })}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      >
                        <option value={4}>4GB</option>
                        <option value={8}>8GB</option>
                        <option value={16}>16GB</option>
                        <option value={32}>32GB</option>
                        <option value={64}>64GB</option>
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        系统盘 (GB) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.systemDisk}
                        onChange={(e) => setFormData({ ...formData, systemDisk: Number(e.target.value) })}
                        placeholder="40-1000"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>

                    {formData.configType === 'gpu' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">GPU型号</label>
                        <select
                          value={formData.gpu}
                          onChange={(e) => setFormData({ ...formData, gpu: e.target.value })}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                        >
                          <option value="">不使用GPU</option>
                          <option value="T4">T4</option>
                          <option value="5070">5070</option>
                          <option value="5090">5090</option>
                        </select>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        下行带宽 (Mbps) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.bandwidth?.download}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            bandwidth: { ...formData.bandwidth!, download: Number(e.target.value) },
                          })
                        }
                        placeholder="1-100"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        上行带宽 (Mbps) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        value={formData.bandwidth?.upload}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            bandwidth: { ...formData.bandwidth!, upload: Number(e.target.value) },
                          })
                        }
                        placeholder="1-100"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      操作系统 <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                      {operatingSystems.map((os) => (
                        <label key={os} className="flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={formData.operatingSystems?.includes(os)}
                            onChange={(e) => {
                              const systems = formData.operatingSystems || [];
                              if (e.target.checked) {
                                setFormData({ ...formData, operatingSystems: [...systems, os] });
                              } else {
                                setFormData({
                                  ...formData,
                                  operatingSystems: systems.filter((s) => s !== os),
                                });
                              }
                            }}
                            className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                          />
                          <span className="ml-2 text-sm text-gray-700">{os}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'sales' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  限购数量 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.limitPerUser}
                  onChange={(e) => setFormData({ ...formData, limitPerUser: Number(e.target.value) })}
                  placeholder="每个用户最多购买数量"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  节点信息 <span className="text-red-500">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-3">
                  {datacenters.map((dc) => (
                    <label key={dc} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.datacenters?.includes(dc)}
                        onChange={(e) => {
                          const centers = formData.datacenters || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, datacenters: [...centers, dc] });
                          } else {
                            setFormData({
                              ...formData,
                              datacenters: centers.filter((c) => c !== dc),
                            });
                          }
                        }}
                        className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">{dc}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  销售时段 <span className="text-red-500">*</span>
                </label>
                <div className="space-y-3">
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.salesPeriod?.type === 'permanent'}
                      onChange={() => setFormData({ ...formData, salesPeriod: { type: 'permanent' } })}
                      className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-2 text-sm text-gray-700">长期有效</span>
                  </label>
                  <label className="flex items-center cursor-pointer">
                    <input
                      type="radio"
                      checked={formData.salesPeriod?.type === 'range'}
                      onChange={() =>
                        setFormData({
                          ...formData,
                          salesPeriod: { type: 'range', startTime: '', endTime: '' },
                        })
                      }
                      className="w-4 h-4 text-teal-500 border-gray-300 focus:ring-teal-500 cursor-pointer"
                    />
                    <span className="ml-2 text-sm text-gray-700">时间范围</span>
                  </label>
                  {formData.salesPeriod?.type === 'range' && (
                    <div className="ml-6 grid grid-cols-2 gap-4">
                      <input
                        type="datetime-local"
                        value={formData.salesPeriod.startTime}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            salesPeriod: { ...formData.salesPeriod!, startTime: e.target.value },
                          })
                        }
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                      <input
                        type="datetime-local"
                        value={formData.salesPeriod.endTime}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            salesPeriod: { ...formData.salesPeriod!, endTime: e.target.value },
                          })
                        }
                        className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  售卖对象类型 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-4">
                  {[
                    { value: 'normal', label: '普通用户' },
                    { value: 'school', label: '学校用户' },
                    { value: 'enterprise', label: '企业用户' },
                  ].map((type) => (
                    <label key={type.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.targetUsers?.includes(type.value as any)}
                        onChange={(e) => {
                          const users = formData.targetUsers || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, targetUsers: [...users, type.value as any] });
                          } else {
                            setFormData({
                              ...formData,
                              targetUsers: users.filter((u) => u !== type.value),
                            });
                          }
                        }}
                        className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'pricing' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">收费方式</label>
                <div className="flex gap-4">
                  {[
                    { value: 'hourly', label: '计时付费' },
                    { value: 'monthly', label: '包月购买' },
                    { value: 'package', label: '包段购买' },
                  ].map((method) => (
                    <label key={method.value} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.pricingMethods?.includes(method.value as any)}
                        onChange={(e) => {
                          const methods = formData.pricingMethods || [];
                          if (e.target.checked) {
                            setFormData({ ...formData, pricingMethods: [...methods, method.value as any] });
                          } else {
                            setFormData({
                              ...formData,
                              pricingMethods: methods.filter((m) => m !== method.value),
                            });
                          }
                        }}
                        className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">{method.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {formData.pricingMethods?.includes('hourly') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    计时价格 (元/小时) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.hourlyPrice}
                    onChange={(e) => setFormData({ ...formData, hourlyPrice: Number(e.target.value) })}
                    placeholder="请输入小时价格"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              )}

              {formData.pricingMethods?.includes('monthly') && (
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-gray-900">套餐管理</h4>
                      <button
                        onClick={() => {
                          setEditingMonthlyPackage(null);
                          setShowMonthlyPackageModal(true);
                        }}
                        className="px-3 py-1.5 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                      >
                        添加套餐
                      </button>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">
                      提示：请先配置"不限时长"套餐作为基准价格
                    </p>
                    {formData.monthlyPackages && formData.monthlyPackages.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50 border-b border-gray-200">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">排序</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">名称</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">时长</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">价格</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">状态</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">操作</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {formData.monthlyPackages
                              .sort((a, b) => a.sort - b.sort)
                              .map((pkg) => (
                                <tr key={pkg.id}>
                                  <td className="px-3 py-2 text-gray-900">{pkg.sort}</td>
                                  <td className="px-3 py-2 text-gray-900">{pkg.name}</td>
                                  <td className="px-3 py-2 text-gray-600">
                                    {pkg.duration === 0 ? '不限时长' : `${pkg.duration}小时`}
                                  </td>
                                  <td className="px-3 py-2 text-gray-900">¥{pkg.price}</td>
                                  <td className="px-3 py-2">
                                    <label className="relative inline-flex items-center cursor-pointer">
                                      <input
                                        type="checkbox"
                                        checked={pkg.isEnabled}
                                        onChange={(e) => handleToggleMonthlyPackage(pkg.id, e.target.checked)}
                                        className="sr-only peer"
                                      />
                                      <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-teal-500 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-teal-500"></div>
                                    </label>
                                  </td>
                                  <td className="px-3 py-2">
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => {
                                          setEditingMonthlyPackage(pkg);
                                          setShowMonthlyPackageModal(true);
                                        }}
                                        className="text-teal-600 hover:text-teal-700 text-xs whitespace-nowrap"
                                      >
                                        编辑
                                      </button>
                                      <button
                                        onClick={() => handleDeleteMonthlyPackage(pkg.id)}
                                        className="text-red-600 hover:text-red-700 text-xs whitespace-nowrap"
                                      >
                                        删除
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-500 text-center py-8">
                        暂无套餐，请点击"添加套餐"按钮创建
                      </div>
                    )}
                  </div>

                  <MonthlyDurationManager
                    durations={formData.monthlyDurations || []}
                    onSave={handleSaveMonthlyDurations}
                    unlimitedPackagePrice={
                      formData.monthlyPackages?.find((p) => p.duration === 0)?.price || 0
                    }
                  />
                </div>
              )}

              {formData.pricingMethods?.includes('package') && (
                <div className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-sm font-medium text-gray-900">包段套餐管理</h4>
                    <button
                      onClick={() => {
                        setEditingPackagePlan(null);
                        setShowPackagePlanModal(true);
                      }}
                      className="px-3 py-1.5 bg-teal-500 text-white text-sm rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
                    >
                      添加套餐
                    </button>
                  </div>
                  {formData.packagePlans && formData.packagePlans.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-50 border-b border-gray-200">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">排序</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">名称</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">价格</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">优惠</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">优惠价</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-700">操作</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {formData.packagePlans
                            .sort((a, b) => a.sort - b.sort)
                            .map((plan) => (
                              <tr key={plan.id}>
                                <td className="px-3 py-2 text-gray-900">{plan.sort}</td>
                                <td className="px-3 py-2 text-gray-900">{plan.name}</td>
                                <td className="px-3 py-2 text-gray-900">¥{plan.price}</td>
                                <td className="px-3 py-2 text-gray-600">
                                  {plan.discount ? (
                                    <>
                                      {plan.discount.type === 'gift' && `赠送${plan.discount.value}小时`}
                                      {plan.discount.type === 'discount' && `${plan.discount.value}折`}
                                      {plan.discount.type === 'reduction' && `减免¥${plan.discount.value}`}
                                    </>
                                  ) : (
                                    '-'
                                  )}
                                </td>
                                <td className="px-3 py-2 text-teal-600 font-medium">
                                  ¥{plan.discountedPrice}
                                </td>
                                <td className="px-3 py-2">
                                  <div className="flex gap-2">
                                    <button
                                      onClick={() => {
                                        setEditingPackagePlan(plan);
                                        setShowPackagePlanModal(true);
                                      }}
                                      className="text-teal-600 hover:text-teal-700 text-xs whitespace-nowrap"
                                    >
                                      编辑
                                    </button>
                                    <button
                                      onClick={() => handleDeletePackagePlan(plan.id)}
                                      className="text-red-600 hover:text-red-700 text-xs whitespace-nowrap"
                                    >
                                      删除
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-500 text-center py-8">
                      暂无套餐，请点击"添加套餐"按钮创建
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3 px-6 pb-6 border-t border-gray-200 pt-4">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
          >
            取消
          </button>
          <button
            onClick={handleSave}
            className="px-6 py-2 bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors whitespace-nowrap"
          >
            保存
          </button>
        </div>
      </div>

      <MonthlyPackageModal
        isOpen={showMonthlyPackageModal}
        package={editingMonthlyPackage}
        existingPackages={formData.monthlyPackages || []}
        onClose={() => {
          setShowMonthlyPackageModal(false);
          setEditingMonthlyPackage(null);
        }}
        onSave={handleSaveMonthlyPackage}
      />

      <PackagePlanModal
        isOpen={showPackagePlanModal}
        plan={editingPackagePlan}
        onClose={() => {
          setShowPackagePlanModal(false);
          setEditingPackagePlan(null);
        }}
        onSave={handleSavePackagePlan}
      />
    </div>
  );
}
